import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Alert, Button, Empty, Select, Skeleton, Tooltip, message } from 'antd'
import { PlusOutlined, SortAscendingOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import {
    DOCUMENT_TEMPLATE_SORT,
    DOCUMENT_TEMPLATE_STATUS,
    deactivateDocumentTemplate,
    duplicateDocumentTemplate,
    getDocumentTemplate,
    listDocumentTemplates,
    reactivateDocumentTemplate,
    setDocumentTemplateDefault,
} from '../../Documents/documentTemplateApi'
import { useLoanOfficerLicenseStates } from '../../Documents/useLoanOfficerLicenseStates'
import { getEffectiveDocumentTemplateStates } from '../../Documents/documentTemplateStates'
import { getDocumentTemplateIncomeTypeLabels } from '../../Documents/documentTemplateIncomeTypes'
import TemplateEditorModal from './TemplateEditorModal'
import { track } from 'statsig'
import { STATSIG, PAGE } from 'statsig/events'
import './index.css'

const SORT_OPTIONS = [
    {
        label: 'Recently updated',
        value: DOCUMENT_TEMPLATE_SORT.UPDATED_DESC,
    },
    {
        label: 'Recently used',
        value: DOCUMENT_TEMPLATE_SORT.LAST_USED_DESC,
    },
]

const formatLastUsed = (value) => {
    if (!value || !dayjs(value).isValid()) return 'Never used'

    const usedAt = dayjs(value)
    const minutes = Math.max(0, dayjs().diff(usedAt, 'minute'))
    if (minutes < 1) return 'Used just now'
    if (minutes < 60) return `Used ${minutes}m ago`

    const hours = dayjs().diff(usedAt, 'hour')
    if (hours < 24) return `Used ${hours}h ago`

    const days = dayjs().diff(usedAt, 'day')
    if (days < 7) return `Used ${days}d ago`

    const weeks = dayjs().diff(usedAt, 'week')
    if (weeks < 5) return `Used ${weeks}w ago`

    const months = dayjs().diff(usedAt, 'month')
    if (months < 12) return `Used ${months}mo ago`

    return `Used ${usedAt.format('MM/DD/YYYY')}`
}

const TemplateStatePills = ({ states }) => {
    return (
        <div className="document-template-state-pills" aria-label="Licensed states">
            {states.map((state) => (
                <span className="document-template-state-pill" key={state}>
                    {state}
                </span>
            ))}
        </div>
    )
}

const TemplateCard = ({
    template,
    availableLicenseStates,
    actionLoading,
    onAction,
    onEdit,
    onUse,
}) => {
    const isActive = template.status === DOCUMENT_TEMPLATE_STATUS.ACTIVE
    const templateStates = getEffectiveDocumentTemplateStates(
        template,
        availableLicenseStates
    )
    const canSetDefault =
        isActive &&
        !template.isDefault &&
        template.canEdit &&
        template.loanTypes.length > 0 &&
        template.purpose &&
        template.incomeTypes.length > 0 &&
        templateStates.length > 0
    const metadata = [
        template.loanTypes.join('/'),
        template.purpose,
        getDocumentTemplateIncomeTypeLabels(template.incomeTypes).join('/'),
    ].filter(Boolean)

    return (
        <article
            className={`document-template-card${
                isActive ? '' : ' document-template-card-inactive'
            }`}
            data-testid={`document-template-${template.id}`}
        >
            <div className="document-template-card-heading">
                <div className="document-template-card-title">
                    <Tooltip title={template.name} placement="topLeft">
                        <h2>{template.name}</h2>
                    </Tooltip>
                    {template.isDefault && (
                        <span className="document-template-default-badge">
                            DEFAULT
                        </span>
                    )}
                    {!isActive && (
                        <span className="document-template-inactive-badge">
                            INACTIVE
                        </span>
                    )}
                </div>
                <span className="document-template-item-count">
                    {template.itemCount} item{template.itemCount === 1 ? '' : 's'}
                </span>
            </div>

            <div className="document-template-metadata">
                {metadata.length > 0 ? metadata.join(' \u00b7 ') : 'No tags'}
            </div>
            <TemplateStatePills states={templateStates} />

            <div className="document-template-card-actions">
                {isActive ? (
                    <>
                        {onUse && (
                            <button
                                className="document-template-text-action document-template-primary-action"
                                disabled={actionLoading}
                                onClick={() => onUse(template)}
                                type="button"
                            >
                                Use in this loan
                            </button>
                        )}
                        {template.canEdit && (
                            <button
                                className="document-template-text-action"
                                disabled={actionLoading}
                                onClick={() => onEdit?.(template)}
                                type="button"
                            >
                                Edit
                            </button>
                        )}
                        <button
                            className="document-template-text-action"
                            disabled={actionLoading}
                            onClick={() => onAction('duplicate', template)}
                            type="button"
                        >
                            Duplicate
                        </button>
                        {canSetDefault && (
                            <button
                                className="document-template-text-action"
                                disabled={actionLoading}
                                onClick={() => onAction('setDefault', template)}
                                type="button"
                            >
                                Set default
                            </button>
                        )}
                        {template.canEdit && (
                            <button
                                className="document-template-text-action document-template-danger-action"
                                disabled={actionLoading}
                                onClick={() => onAction('deactivate', template)}
                                type="button"
                            >
                                Deactivate
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        {template.canEdit && (
                            <button
                                className="document-template-text-action document-template-primary-action"
                                disabled={actionLoading}
                                onClick={() => onAction('reactivate', template)}
                                type="button"
                            >
                                Reactivate
                            </button>
                        )}
                        <button
                            className="document-template-text-action"
                            disabled={actionLoading}
                            onClick={() => onAction('duplicate', template)}
                            type="button"
                        >
                            Duplicate
                        </button>
                    </>
                )}
                <span className="document-template-last-used">
                    {formatLastUsed(template.lastUsedAt)}
                </span>
            </div>
        </article>
    )
}

const TemplateLibrary = ({ loanID }) => {
    const history = useHistory()
    const {
        error: licenseStatesError,
        loading: licenseStatesLoading,
        options: licenseStateOptions,
        retry: retryLicenseStates,
        states: availableLicenseStates,
    } = useLoanOfficerLicenseStates(true)
    const [templates, setTemplates] = useState([])
    const [selectedState, setSelectedState] = useState('')
    const [selectedSort, setSelectedSort] = useState(
        DOCUMENT_TEMPLATE_SORT.UPDATED_DESC
    )
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [actionID, setActionID] = useState('')
    const [editorOpen, setEditorOpen] = useState(false)
    const [editorTemplate, setEditorTemplate] = useState(null)

    const loadTemplates = useCallback(async (signal) => {
        setLoading(true)
        setError('')
        try {
            const result = await listDocumentTemplates({
                status: DOCUMENT_TEMPLATE_STATUS.ALL,
                sort: selectedSort,
                signal,
            })
            setTemplates(result)
        } catch (requestError) {
            if (requestError.name !== 'AbortError') {
                setError(requestError.message || 'Unable to load templates.')
            }
        } finally {
            if (!signal?.aborted) setLoading(false)
        }
    }, [selectedSort])

    useEffect(() => {
        track(STATSIG.COMMON.VIEW, PAGE.DOCUMENT_TEMPLATES, { page: PAGE.DOCUMENT_TEMPLATES })
    }, [])

    useEffect(() => {
        const controller = new AbortController()
        loadTemplates(controller.signal)
        return () => controller.abort()
    }, [loadTemplates])

    useEffect(() => {
        if (
            selectedState &&
            !availableLicenseStates.includes(selectedState)
        ) {
            setSelectedState('')
        }
    }, [availableLicenseStates, selectedState])

    const stateFilterOptions = useMemo(
        () =>
            licenseStateOptions.map((option) => ({
                ...option,
                selectionLabel: option.value,
            })),
        [licenseStateOptions]
    )

    const visibleTemplates = useMemo(() => {
        return templates
            .filter(
                (template) =>
                    !selectedState ||
                    getEffectiveDocumentTemplateStates(
                        template,
                        availableLicenseStates
                    ).includes(selectedState)
            )
            .sort((left, right) => {
                const leftInactive =
                    left.status === DOCUMENT_TEMPLATE_STATUS.INACTIVE
                const rightInactive =
                    right.status === DOCUMENT_TEMPLATE_STATUS.INACTIVE
                return Number(leftInactive) - Number(rightInactive)
            })
    }, [availableLicenseStates, selectedState, templates])

    const handleAction = async (action, template) => {
        setActionID(template.id)
        try {
            switch (action) {
                case 'duplicate': {
                    await duplicateDocumentTemplate(template.id)
                    track(STATSIG.DOCUMENT_TEMPLATES.INSERT, 'duplicate', { type: 'duplicate' })
                    message.success(`"${template.name}" duplicated.`)
                    break
                }
                case 'deactivate':
                    await deactivateDocumentTemplate(template.id)
                    track(STATSIG.DOCUMENT_TEMPLATES.STATUS, 'deactivate', { type: 'deactivate' })
                    message.success(`"${template.name}" deactivated.`)
                    break
                case 'reactivate':
                    await reactivateDocumentTemplate(template.id)
                    track(STATSIG.DOCUMENT_TEMPLATES.STATUS, 'reactivate', { type: 'reactivate' })
                    message.success(`"${template.name}" reactivated.`)
                    break
                case 'setDefault':
                    await setDocumentTemplateDefault(template.id, true)
                    track(STATSIG.DOCUMENT_TEMPLATES.DEFAULT)
                    message.success(`"${template.name}" is now the default.`)
                    break
                default:
                    return
            }
            await loadTemplates()
        } catch (requestError) {
            message.error(
                requestError.message || 'Unable to update the template.'
            )
        } finally {
            setActionID('')
        }
    }

    const openNewTemplate = () => {
        setEditorTemplate(null)
        setEditorOpen(true)
    }

    const openEditTemplate = async (template) => {
        setActionID(template.id)
        try {
            const detail = await getDocumentTemplate(template.id)
            setEditorTemplate(detail)
            setEditorOpen(true)
        } catch (requestError) {
            message.error(
                requestError.message || 'Unable to load the template.'
            )
        } finally {
            setActionID('')
        }
    }

    const handleTemplateSaved = async (savedTemplate) => {
        if (editorTemplate) {
            track(STATSIG.DOCUMENT_TEMPLATES.UPDATE)
        } else {
            track(STATSIG.DOCUMENT_TEMPLATES.INSERT, 'new', { type: 'new' })
        }
        setEditorOpen(false)
        setEditorTemplate(null)
        await loadTemplates()
    }

    const useTemplateInLoan = (template) => {
        if (!loanID) return

        history.push({
            pathname: `/app/borrower/${loanID}`,
            state: {
                documentTemplateID: template.id,
                openDocumentRequest: true,
            },
        })
    }

    const pageLoading = loading || licenseStatesLoading

    return (
        <main className="document-template-library">
            <div className="document-template-page-heading">
                <h1>Document templates</h1>
                <Button
                    className="document-template-new-button"
                    icon={<PlusOutlined />}
                    onClick={openNewTemplate}
                    type="primary"
                >
                    New template
                </Button>
            </div>

            <p className="document-template-description">
                Reusable document-request checklists. Templates are tagged by loan
                type, purpose, income type, and the state(s) you're licensed in
                {' \u2014 '}so the right one is auto-suggested when you open a
                request on a matching loan.
            </p>

            <div className="document-template-toolbar">
                <div className="document-template-state-control">
                    <span>State</span>
                    <Select
                        allowClear
                        aria-label="Filter templates by state"
                        className="document-template-state-select"
                        disabled={
                            licenseStatesLoading || Boolean(licenseStatesError)
                        }
                        popupMatchSelectWidth={220}
                        loading={licenseStatesLoading}
                        onChange={setSelectedState}
                        optionFilterProp="label"
                        optionLabelProp="selectionLabel"
                        options={stateFilterOptions}
                        placeholder="Select state"
                        showSearch
                        value={selectedState || undefined}
                    />
                    {licenseStatesError && (
                        <Button
                            className="document-template-state-retry"
                            onClick={retryLicenseStates}
                            type="link"
                        >
                            Retry states
                        </Button>
                    )}
                </div>
                <div className="document-template-sort-control">
                    <span>Sort by</span>
                    <Select
                        aria-label="Sort templates"
                        onChange={setSelectedSort}
                        options={SORT_OPTIONS}
                        suffixIcon={<SortAscendingOutlined />}
                        value={selectedSort}
                    />
                </div>
            </div>

            {error && (
                <Alert
                    action={<Button onClick={() => loadTemplates()}>Retry</Button>}
                    description={error}
                    message="Templates could not be loaded"
                    showIcon
                    type="error"
                />
            )}

            {!error && pageLoading && (
                <div className="document-template-grid" aria-busy="true">
                    {[0, 1, 2, 3].map((item) => (
                        <div className="document-template-card" key={item}>
                            <Skeleton active paragraph={{ rows: 3 }} />
                        </div>
                    ))}
                </div>
            )}

            {!error && !pageLoading && visibleTemplates.length > 0 && (
                <div className="document-template-grid">
                    {visibleTemplates.map((template) => (
                        <TemplateCard
                            actionLoading={actionID === template.id}
                            availableLicenseStates={availableLicenseStates}
                            key={template.id}
                            onAction={handleAction}
                            onEdit={openEditTemplate}
                            onUse={loanID ? useTemplateInLoan : undefined}
                            template={template}
                        />
                    ))}
                </div>
            )}

            {!error && !pageLoading && visibleTemplates.length === 0 && (
                <div className="document-template-empty">
                    <Empty description="No templates found" />
                </div>
            )}

            <TemplateEditorModal
                onCancel={() => {
                    setEditorOpen(false)
                    setEditorTemplate(null)
                }}
                onSaved={handleTemplateSaved}
                open={editorOpen}
                template={editorTemplate}
            />
        </main>
    )
}

export default TemplateLibrary
